import React, { useReducer, useState, useEffect, useMemo } from 'react'
import Big from 'big.js'

import { PLEDGE_MAX } from 'src/components/Validation'
import Form       from './Form'
import Info       from './Info'
import Panel      from '../Panel'
import useRecord            from '../fiat/useRecord'
import useValidation from '../useValidation'
import useBeneficiarySelect from '../useBeneficiarySelect'

import csrfHeaders  from 'src/utils/csrfHeaders'
import prettyAmount from 'src/utils/prettyAmount'

const modeOpposites = { fiat: 'crypto', crypto: 'fiat' }

const reducer = (state, action) => {
  switch (action.type) {
    case 'setFieldValue':
      return { ...state, [action.field]: action.value }
    case 'setAmounts': {
      const amount = action.hasOwnProperty('value')
      ? action.value
      : state[`${state.mode}_amount`]

      const updates = (() => {
        switch (state.mode) {
          case 'fiat': return {
            fiat_amount: amount,
            crypto_amount: Big(amount || 0).times(action.exchangeRate).round(8).toString(),
          }
          case 'crypto': return {
            crypto_amount: amount,
            fiat_amount: Big(amount || 0).times(Big('1').div(action.exchangeRate)).round(action.fiatCurrency.exponent).toString(),
          }
        }
      })()

      return { ...state, ...updates }
    }
    case 'setAddress':
      return { ...state, ...action.value }
    default:
      throw new Error('Invalid reducer action')
  }
}

const App = ({
  partner_item, partner_items, goal_partner_item,
  i18n, data_sharing, defaultShares, setDonation,
  amount, order, min_amount, widget_id, partner, donationTags, embeddedOn,
  exchange_rate, exchange_rate_url, fiat_currency, crypto_currency, currencies,
  submitUrl, show_input_labels, commonInputProps, partner_is_pledge,
  foundation, collect_address, collect_phone,
  accounts, top_accounts, statusUrl, original_fundraiser_id, plugins, elements,
  token, template, tribute, session, donationsPath, disable_donation_split,
  panel: topPanel, blank_modal_src, widget_instance_token,
}) => {
  const [ panel, setPanel ] = useState('form')
  const [ stepTwo, setStepTwo ] = useState()
  const [ loading, setLoading ] = useState(false)
  const [ shares, setShares ] = useState(order.shares)

  const { partnerItem, projectId, ...beneficiarySelect } = useBeneficiarySelect({
    i18n, commonInputProps, partner_item, partner_items, goal_partner_item,
    disable_donation_split,
  })

  const [ exchangeRate, setExchangeRate ] = useState(exchange_rate)
  const [ state, dispatch ] = useReducer(reducer, null, () => ({
    firstName:        order.user_first_name   || '',
    lastName:         order.user_last_name    || '',
    email:            order.email             || '',
    phone:            order.user_phone_number || '',
    mode:             'fiat',
    fiat_currency:    fiat_currency.iso_code,
    fiat_amount:      prettyAmount(amount),
    crypto_currency:  crypto_currency,
    crypto_amount:    Big(amount).times(exchangeRate).round(8).toString(),
  }))

  const fiatCurrency = useMemo(() =>
    currencies.find(currency => currency.iso_code == state.fiat_currency)
  , [ state.fiat_currency ])

  const usdAmount = useMemo(() =>
    Big(state.fiat_amount || 0).div(fiatCurrency.rate)
  , [ state.fiat_amount, fiatCurrency.rate ])

  const donation = {
    state, dispatch, shares, setShares, usdAmount,
    collectAddress: collect_address || usdAmount.gte(5000),
  }

  // Synthetic shape for the shared useRecord (wallet does the same):
  // it expects donation.state.amount + donation.currency.id, while
  // crypto's local state uses fiat_amount + fiat_currency (an iso code).
  // `total` and `tip` are included for completeness even though
  // principalOnly: true on the saveDraft call below short-circuits
  // the only reads of them in draftProps today — if anyone later
  // reads donation.total or donation.tip outside that guard, crypto
  // would otherwise silently break. Cheap insurance.
  const recordDonation = {
    state: { ...state, amount: state.fiat_amount },
    shares,
    currency: { id: state.fiat_currency.toLowerCase() },
    total: state.fiat_amount,
    tip: 0,
  }
  const record = useRecord({
    i18n, order, partner, partnerItem, projectId, session, tribute,
    donationTags, embeddedOn, donation: recordDonation, plugins, donationsPath,
    widget: { token, template },
  })

  const minAmount = useMemo(() =>
    Big(min_amount).times(fiatCurrency.rate).round(fiatCurrency.exponent, 3).toString()
  , [ min_amount, fiatCurrency ])

  const amounts = {
    fiat: {
      amount: state.fiat_amount,
      currency: fiatCurrency,
      isFiat: true,
    },
    crypto: {
      amount: state.crypto_amount,
      currency: {
        iso_code: state.crypto_currency,
        exponent: 8,
      }
    },
  }
  const displayAmount = amounts[state.mode]
  const convertedAmount = amounts[modeOpposites[state.mode]]

  const validation = useValidation({
    i18n,
    fieldSet: {
      firstName:  { required: true },
      lastName:   { required: true },
      email:      { required: true, maxLength: 75, email: true },
      fiat_amount: { required: true, min: minAmount, max: PLEDGE_MAX },
      crypto_amount:    { required: true },
      crypto_currency:  { required: true },
    }
  })

  useEffect(() => {
    validation.resetErrorsFor('fiat_amount')
    validation.resetErrorsFor('crypto_amount')
  }, [ state.fiat_amount, state.crypto_amount ])

  const inputProps = (id, label, { showInputLabels = show_input_labels } = {}) => ({
    ...commonInputProps(label, showInputLabels),
    id,
    value: state[id],
    onChange: e => {
      dispatch({ type: 'setFieldValue', field: id, value: e.target.value })
      validation.resetErrorsFor(id)
    },
  })

  const setFiatCurrency = value =>
    dispatch({ type: 'setFieldValue', field: 'fiat_currency', value })

  const setCryptoCurrency = value =>
    dispatch({ type: 'setFieldValue', field: 'crypto_currency', value })

  const toggleMode = () => dispatch({
    type: 'setFieldValue',
    field: 'mode',
    value: modeOpposites[state.mode],
  })

  const handleAmountChange = e =>
    dispatch({ type: 'setAmounts', value: e.target.value, exchangeRate, fiatCurrency })

  // Make sure all validations are run
  const validateAll = () => {
    const results = [
      beneficiarySelect.validate(),
      validation.validate(state),
      tribute.validate(),
      plugins.validate({ donation }),
    ]
    return results.length == results.filter(Boolean).length
  }

  const mapParams = () => {
    const { firstName, lastName, address, ...rest } = state

    return {
      ...rest,
      usd_amount: usdAmount.round(2),
      first_name: firstName,
      last_name: lastName,
      donor_address: address,
      payment_widget_config_id: widget_id,
      partner_key: partner.key,
      external_id: partnerItem.external_id,
      project_id: projectId,
      organization_id: partnerItem.beneficiary.id,
      plugin_data: plugins.dataParam,
      shares,
      original_fundraiser_id, // need this for peer orders
    }
  }

  // Two-step submit: first persist the donation intent on the unified
  // draft endpoint (so plugin_data + donor info land on an Order before
  // the donor sends crypto), then POST the crypto-specific metadata so
  // the new CryptoInput attaches to the draft via order_id.
  const validateAndSubmit = async ({ complete }) => {
    if (!validateAll() || !complete) return
    setLoading(true)
    try {
      const { errors: draftErrors, token: orderToken } = await record.saveDraft({
        principalOnly: true,
        order: { payment_source: 'crypto' },
      })
      if (draftErrors) return validation.setErrors(draftErrors)

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          ...mapParams(),
          order_id: orderToken,
          donation_tags: donationTags,
          embedded_on: embeddedOn,
        }),
      })
      const json = await response.json()
      if (response.ok) {
        setStepTwo(json)
        setPanel('info')
      } else {
        validation.setErrors(json.errors)
      }
    } catch (error) {
      console.error(error)
      const msg = 'A network or server error occured. Please try again. Contact us if the issue persists.'
      validation.setErrors({ base: [ msg ] })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (loading) return

    if (donation.collectAddress)
      elements.getElement('address').getValue().then(validateAndSubmit)
    else validateAndSubmit({ complete: true })
  }

  const setDonationWithPartnerItem = json => setDonation({ ...json, partnerItem })

  useEffect(() => {
    if (stepTwo) return

    const updateExchangeRate = () => {
      const url = [
        exchange_rate_url,
        new URLSearchParams({
          fiat_currency: state.fiat_currency,
          crypto_currency: state.crypto_currency,
        })
      ].join('?')

      fetch(url, { headers: { Accept: 'application/json' } })
      .then(response => response.json())
      .then(json => setExchangeRate(json.exchange_rate))
    }

    updateExchangeRate()
    const interval = setInterval(updateExchangeRate, 30000)
    return () => clearInterval(interval)
  }, [ stepTwo, state.fiat_currency, state.crypto_currency ])

  useEffect(() => {
    dispatch({ type: 'setAmounts', exchangeRate, fiatCurrency })
  }, [ exchangeRate, fiatCurrency ])

  return (
    <Panel.Collection panel={panel} flush>
      <Panel name="form">
        <Form
          i18n={i18n}
          commonInputProps={commonInputProps}
          inputProps={inputProps}
          partnerItem={partnerItem}
          beneficiarySelect={beneficiarySelect}
          shares={shares}
          setShares={setShares}
          defaultShares={defaultShares}
          loading={loading}
          foundation={foundation}
          collect_phone={collect_phone}
          partner={partner}
          partner_is_pledge={partner_is_pledge}
          data_sharing={data_sharing}
          handleSubmit={handleSubmit}
          errors={validation.errors}
          displayAmount={displayAmount}
          convertedAmount={convertedAmount}
          handleAmountChange={handleAmountChange}
          toggleMode={toggleMode}
          donation={donation}
          currencies={currencies}
          setFiatCurrency={setFiatCurrency}
          accounts={accounts}
          top_accounts={top_accounts}
          setCryptoCurrency={setCryptoCurrency}
          plugins={plugins}
          tribute={tribute}
          session={session}
          topPanel={topPanel}
          blank_modal_src={blank_modal_src}
          widget_instance_token={widget_instance_token}
        />
      </Panel>
      <Panel name="info">
        <Info
          state={state}
          stepTwo={stepTwo}
          statusUrl={statusUrl}
          setDonation={setDonationWithPartnerItem}
          errors={validation.errors}
        />
      </Panel>
    </Panel.Collection>
  )
}

export default App
